import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { useMembersStore } from "features/members";
import { useFormPersistence } from "hooks/useFormPersistence";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSteps } from "react-step-builder";
import { previousChurchInfoSchema } from "./schema";
import { PreviousChurchInfoSection } from "./sections";

export function PublicPreviousChurchInfoForm() {
  const memberData = useMembersStore(state => state.memberData);
  const setMemberData = useMembersStore(state => state.setMemberData);
  const setLetterImage = useMembersStore(state => state.setLetterImage);

  const { t } = useTranslation();
  const { prev, next } = useSteps();
  const { saveFormData, loadFormData } = useFormPersistence();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch
  } = useForm({
    defaultValues: {
      transfer_from_other_church: memberData.transfer_from_other_church
        ? memberData.transfer_from_other_church
        : "",
      previous_church_name: memberData.previous_church_name
        ? memberData.previous_church_name
        : "",
      date_of_resignation: memberData.date_of_resignation
        ? memberData.date_of_resignation
        : null,
      pastor_title: memberData.pastor_title ? memberData.pastor_title : "",
      pastor_full_name: memberData.pastor_full_name
        ? memberData.pastor_full_name
        : "",
      pastor_phone_number: memberData.pastor_phone_number
        ? memberData.pastor_phone_number
        : "",
      reason_for_resignation: memberData.reason_for_resignation
        ? memberData.reason_for_resignation
        : ""
    },
    resolver: yupResolver(previousChurchInfoSchema)
  });

  // Load saved form data when component mounts
  useEffect(() => {
    const savedData = loadFormData();
    if (savedData) {
      if (savedData.previousChurchInfo) {
        reset(savedData.previousChurchInfo);
      }
      if (savedData.letter_of_resignation) {
        setLetterImage(savedData.letter_of_resignation);
      }
    }
  }, [loadFormData, reset, setLetterImage]);

  // Save form data whenever it changes
  useEffect(() => {
    const subscription = watch(data => {
      saveFormData({
        previousChurchInfo: data,
        letter_of_resignation: memberData.letter_of_resignation
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, saveFormData]);

  // Save letter separately when it changes (including when removed)
  useEffect(() => {
    const formData = watch();
    // Only save if we have form data
    if (formData && Object.keys(formData).length > 0) {
      saveFormData({
        previousChurchInfo: formData,
        letter_of_resignation: memberData.letter_of_resignation
      });
    }
  }, [memberData.letter_of_resignation]);

  const onSubmit = data => {
    setMemberData(data);
    next();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PreviousChurchInfoSection
        register={register}
        errors={errors}
        control={control}
        watch={watch}
      />
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 8 }}
      >
        <Button
          type="button"
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={prev}
        >
          {t("form.common.back")}
        </Button>
        <Button type="submit" variant="contained" endIcon={<ArrowForward />}>
          {t("form.common.next")}
        </Button>
      </Stack>
    </form>
  );
}
